import {
  HoldingsAndTransferBalancesReconciliation,
  useGetByTenantRiskmanagementLatestAssetspricesQuery
} from 'api/rtk-api'
import clsx from 'clsx'
import {
  AssetInformationModal,
  CurrencyField,
  LinkButton,
  NumericalDisplayField
} from 'components/core-v2'
import { useEffect, useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { formatCurrencyFull } from 'utilities/currency'

import { CURRENCY, TENANT } from '../../../const'
import { ReducerActionType } from '../unit-transfers-main/unit-transfers-main'
import { AssetGridData } from '../unit-transfers-utilities/map-data'
import classes from './unit-transfers-asset.module.scss'
import { useUnitTransfersContext } from './unit-transfers-context'

interface UnitTransfersAssetProps {
  asset: AssetGridData
  finswitch: HoldingsAndTransferBalancesReconciliation
}

function areAllFieldValuesValid(fieldValues: ({ numerical: number } | undefined)[]) {
  return fieldValues && fieldValues.every(value => value?.numerical > 0)
}

function areAllFieldsPopulated(fieldValues: ({ numerical: number } | undefined)[]) {
  return fieldValues && fieldValues.every(value => value?.numerical !== undefined)
}

function getFinswitchValue(id: string, finswitch: HoldingsAndTransferBalancesReconciliation) {
  const finswitchEntry = finswitch?.entries.find(ent => ent.assetId === id)
  const availableFinswitchUnits = finswitchEntry
    ? finswitchEntry.executionAgentHoldings - finswitchEntry.internalHoldings
    : 0
  return availableFinswitchUnits <= 0 ? 0 : availableFinswitchUnits
}

function hasNoValues(unitValues: { numerical?: number }[]) {
  return unitValues.every(value => !value?.numerical)
}

export const UnitTransfersAsset = ({ asset, finswitch }: UnitTransfersAssetProps) => {
  const { data: currentPrices } = useGetByTenantRiskmanagementLatestAssetspricesQuery({
    tenant: TENANT
  })
  const price = currentPrices?.find(price => price.assetName === asset.assetName)?.currentPrice
    .price
  const {
    allFieldIds,
    assetId: id,
    cells,
    defaultUnitFieldValues,
    priceFieldIds,
    unitFieldIds
  } = asset
  const { dispatch } = useUnitTransfersContext()
  const [showModal, setShowModal] = useState(false)

  const fieldValues = useWatch({ name: allFieldIds })
  const priceFieldValues = useWatch({ name: priceFieldIds })
  const unitFieldValues = useWatch({ name: unitFieldIds })
  const availableFinswitchUnits = getFinswitchValue(id, finswitch)

  const { columnTotal, difference } = useMemo(() => {
    const values = hasNoValues(unitFieldValues) ? defaultUnitFieldValues : unitFieldValues
    const columnTotal = values.reduce((sum, x) => sum + x.numerical, 0)
    const difference = availableFinswitchUnits - columnTotal
    return { columnTotal, difference }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFinswitchUnits, ...unitFieldValues])

  const { baseValue } = useMemo(() => {
    const unitValues = hasNoValues(unitFieldValues) ? defaultUnitFieldValues : unitFieldValues
    const baseValue: number =
      priceFieldValues?.reduce((sum, x, i) => {
        const value = (x?.numerical ? x.numerical / 100 : 0) * (unitValues[i]?.numerical ?? 0)
        return sum + value
      }, 0) ?? 0
    return { baseValue }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...priceFieldValues, ...unitFieldValues])

  useEffect(() => {
    if (difference >= 0 && areAllFieldValuesValid(fieldValues)) {
      dispatch({ type: ReducerActionType.Completed, id })
    } else if (areAllFieldsPopulated(fieldValues)) {
      dispatch({ type: ReducerActionType.Invalid, id })
    } else {
      dispatch({ type: ReducerActionType.Partial, id })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...fieldValues, dispatch, difference, id])

  return (
    <div
      className={clsx(classes.root, classes.assetColumn, {
        [classes.negative]: difference < 0,
        [classes.invalid]: difference < 0 && areAllFieldsPopulated(fieldValues),
        [classes.valid]: difference >= 0 && areAllFieldValuesValid(fieldValues)
      })}
    >
      <div className={clsx(classes.assetTitles)}>
        <div className={clsx(classes.assetName)}>
          <LinkButton underline onClick={() => setShowModal(true)}>
            {asset.assetName}
          </LinkButton>
          <AssetInformationModal
            assetId={id}
            isOpen={showModal}
            onClose={() => setShowModal(false)}
          />
        </div>
        <div className={clsx(classes.headings)}>
          <span>
            <div>Original purchase</div>
            <div>price / unit</div>
          </span>
          <span>Units</span>
        </div>
      </div>
      {cells.map(cell => {
        if (cell.isEmpty) return <div key={cell.id} className={clsx(classes.emptyCell)} />
        return (
          <div key={cell.id} className={clsx(classes.cell)}>
            <CurrencyField
              className={classes.price}
              decimalPlaces={4}
              id={`${cell.id}-${id}-price`}
              symbol="Zac"
            />
            <NumericalDisplayField
              className={classes.unit}
              decimalPlaces={6}
              defaultValue={cell.transferUnits.units}
              id={`${cell.id}-${id}-unit`}
            />
          </div>
        )
      })}
      <div className={clsx(classes.totals)}>
        <div>
          {columnTotal.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6
          })}
        </div>
        <div>
          {baseValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6
          })}
        </div>
        <div>
          {availableFinswitchUnits.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6
          })}
        </div>
        <div className={clsx(classes.difference)}>
          {difference.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6
          })}
        </div>
        <div>{formatCurrencyFull(price, CURRENCY, null, true)}</div>
      </div>
    </div>
  )
}
